import React, { useCallback, useEffect, useState } from "react";
import { Button, Progress, Spin, Table, Tag, Tooltip } from "antd";
import { CheckSquareOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SectionCard from "../shared/SectionCard";
import { EditAccess } from "../../../components/accessControl/AccessControl";
import { taskActualApis } from "../../../api/TaskSchedule";
import TaskActualFormModal from "./TaskActualFormModal";

// "Monthly Actual Execution" section on a task — the executed twin of
// TaskScheduleSection, rendered right below it. Shows month rows with target
// (scheduled) vs actual quantity and an execution % chip. Only quantity-based
// tasks can capture actuals. Under-execution is the normal state; only
// over-execution gets an amber chip.

interface ActualMonthRow {
  actualMonth: string;
  actualQty: string;
  scheduledQty: string;
  remarks?: string;
}

const fmtQty = (value: number) =>
  value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

const TaskActualSection = ({
  task,
  canEdit,
}: {
  task: any;
  canEdit?: boolean;
}) => {
  const [months, setMonths] = useState<ActualMonthRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isQuantityTask =
    (task?.workType || "") !== "Open" &&
    task?.workQuantity !== null &&
    task?.workQuantity !== undefined;
  const workQuantity = Number(task?.workQuantity) || 0;
  const uom = task?.workUomShortCode || task?.workUomName || "";

  const load = useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId || !isQuantityTask) return;
    try {
      setLoading(true);
      const res = await taskActualApis.getByTask(taskId);
      if (res?.status === true) {
        setMonths(Array.isArray(res?.data?.months) ? res.data.months : []);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.taskId, isQuantityTask]);

  useEffect(() => {
    setMonths([]);
    load();
  }, [load]);

  const actualized = months.filter((m) => Number(m.actualQty) > 0 || m.remarks);
  const total = months.reduce((acc, m) => acc + (Number(m.actualQty) || 0), 0);
  const executionPct =
    workQuantity > 0 ? Number(((total / workQuantity) * 100).toFixed(2)) : 0;
  const overBy = workQuantity > 0 ? Number((total - workQuantity).toFixed(2)) : 0;
  const overExecuted = workQuantity > 0 && overBy > 1;

  return (
    <SectionCard
      title={
        <span>
          <CheckSquareOutlined style={{ marginRight: 6 }} />
          Monthly Actual Execution{" "}
          {actualized.length ? (
            <span style={{ color: "#8094ae", fontWeight: 400 }}>
              ({actualized.length} month{actualized.length === 1 ? "" : "s"})
            </span>
          ) : null}
        </span>
      }
      style={{ marginBottom: 12 }}
      extra={
        isQuantityTask && canEdit ? (
          <EditAccess moduleName="Tasks">
            <Button
              size="small"
              icon={actualized.length ? <EditOutlined /> : <PlusOutlined />}
              onClick={() => setEditOpen(true)}
            >
              {actualized.length ? "Edit Actuals" : "Add Actuals"}
            </Button>
          </EditAccess>
        ) : null
      }
    >
      {!isQuantityTask ? (
        <span style={{ color: "#8094ae", fontSize: 12 }}>
          Only quantity-based tasks can capture actuals.
        </span>
      ) : loading ? (
        <Spin size="small" />
      ) : !actualized.length ? (
        <span style={{ color: "#8094ae", fontSize: 12 }}>
          No actual execution captured yet.
        </span>
      ) : (
        <>
          <Table
            size="small"
            pagination={false}
            rowKey="actualMonth"
            dataSource={actualized}
            columns={[
              {
                title: "Month",
                dataIndex: "actualMonth",
                key: "actualMonth",
                render: (value: string, row: ActualMonthRow) => (
                  <span style={{ color: "#022c57", fontWeight: 500 }}>
                    {dayjs(value).format("MMM YYYY")}
                    {row.remarks ? (
                      <Tooltip title={row.remarks}>
                        <Tag
                          color="blue"
                          style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}
                        >
                          note
                        </Tag>
                      </Tooltip>
                    ) : null}
                  </span>
                ),
              },
              {
                title: `Planned${uom ? ` (${uom})` : ""}`,
                dataIndex: "scheduledQty",
                key: "scheduledQty",
                align: "right" as const,
                render: (value: string) =>
                  Number(value) > 0 ? (
                    <span style={{ color: "#8094ae" }}>{fmtQty(Number(value))}</span>
                  ) : (
                    <span style={{ color: "#c9d2e0" }}>—</span>
                  ),
              },
              {
                title: `Actual${uom ? ` (${uom})` : ""}`,
                dataIndex: "actualQty",
                key: "actualQty",
                align: "right" as const,
                render: (value: string) => fmtQty(Number(value) || 0),
              },
            ]}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 8,
              fontSize: 12,
              color: "#526484",
              gap: 12,
            }}
          >
            <span>
              Executed <b style={{ color: "#022c57" }}>{fmtQty(total)}</b>
              {workQuantity > 0 ? (
                <>
                  {" "}
                  of {fmtQty(workQuantity)}
                  {uom ? ` ${uom}` : ""}
                </>
              ) : null}
            </span>
            {workQuantity > 0 &&
              (overExecuted ? (
                <Tag color="gold">Over by +{fmtQty(overBy)}</Tag>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
                  <Progress
                    percent={Math.min(100, executionPct)}
                    size="small"
                    showInfo={false}
                    style={{ margin: 0, flex: 1 }}
                  />
                  <span style={{ color: "#022c57", fontWeight: 500 }}>{executionPct}%</span>
                </span>
              ))}
          </div>
        </>
      )}

      <TaskActualFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        task={{
          taskId: Number(task?.taskId),
          taskName: task?.taskName,
          workQuantity: task?.workQuantity,
          workUomName: uom,
        }}
        initialMonths={months}
        onSaved={load}
      />
    </SectionCard>
  );
};

export default TaskActualSection;
